import React, { useEffect, useState } from 'react';
import homepageApi from '../../api/homepage';
import homepageSettingsApi from '../../api/homepageSettings';
import Loader from '../../common/UI/Loader';
import { toastError, toastSuccess } from '../../common/UI/Toasts';
import ExpertsManager from './ExpertsManager';
import TestimonialsManager from './TestimonialsManager';
import FAQManager from './FAQManager';
import HeroImagesManager from './HeroImagesManager';
import TrustFactorsManager from './TrustFactorsManager';
import AnalyticsManager from './AnalyticsManager';
import NewsBrandsManager from './NewsBrandsManager';
import PaymentOptionsManager from './PaymentOptionsManager';
import SingleSectionEditor from './SingleSectionEditor';
import NestedSectionEditor from './NestedSectionEditor';
import LandingPageSettings from './LandingPageSettings';
import ReactSwitch from 'react-switch';

// Section configuration
const SECTION_CONFIG = {
  hero_images: { name: 'Hero Images', type: 'list', component: 'HeroImagesManager' },
  hero_right_section: {
    name: 'Hero Right Section',
    type: 'single',
    component: 'SingleSectionEditor',
  },
  trust_factors: { name: 'Trust Factors', type: 'list', component: 'TrustFactorsManager' },
  how_it_works: { name: 'How It Works', type: 'nested', component: 'NestedSectionEditor' },
  testimonials: { name: 'Testimonials', type: 'nested', component: 'NestedSectionEditor' },
  why_tryageless: { name: 'Why Try Ageless', type: 'single', component: 'SingleSectionEditor' },
  analytics: { name: 'Analytics', type: 'list', component: 'AnalyticsManager' },
  news_brands: { name: 'News Brands', type: 'list', component: 'NewsBrandsManager' },
  experts: { name: 'Experts', type: 'list', component: 'ExpertsManager' },
  proud_section: { name: 'Proud Section', type: 'single', component: 'SingleSectionEditor' },
  comparison: { name: 'Comparison', type: 'nested', component: 'NestedSectionEditor' },
  faq: { name: 'FAQ', type: 'list', component: 'FAQManager' },
  caring_section: { name: 'Caring Section', type: 'single', component: 'SingleSectionEditor' },
  payment_options: { name: 'Payment Options', type: 'list', component: 'PaymentOptionsManager' },
};

const LandingPageCMS = () => {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'edit'
  const [showSettings, setShowSettings] = useState(false);
  const [landingPageEnabled, setLandingPageEnabled] = useState(false);

  useEffect(() => {
    fetchSections();
    fetchLandingPageStatus();
  }, []);

  const fetchSections = async () => {
    setLoading(true);
    try {
      const response = await homepageApi.getAllHomepageSections();
      setSections(response?.data?.data || []);
    } catch (error) {
      toastError('Failed to fetch sections');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLandingPageStatus = async () => {
    try {
      const response = await homepageSettingsApi.getSettings();
      setLandingPageEnabled(response?.data?.data?.landingPageEnabled || false);
    } catch (error) {
      console.error('Failed to fetch landing page status:', error);
    }
  };

  const handleSectionClick = (section) => {
    setSelectedSection(section);
    setViewMode('edit');
  };

  const handleBackToList = () => {
    setViewMode('list');
    setSelectedSection(null);
    fetchSections();
  };

  const handleToggleStatus = async (sectionKey, currentStatus) => {
    setLoading(true);
    try {
      await homepageApi.updateSectionStatus(sectionKey, { status: !currentStatus });
      toastSuccess('Section status updated successfully');
      fetchSections();
    } catch (error) {
      toastError('Failed to update section status');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const renderSectionManager = () => {
    if (!selectedSection) return null;

    const config = SECTION_CONFIG[selectedSection.sectionKey];
    const props = {
      section: selectedSection,
      backToList: handleBackToList,
      refreshSections: fetchSections,
    };

    switch (config.component) {
      case 'HeroImagesManager':
        return <HeroImagesManager {...props} />;
      case 'TrustFactorsManager':
        return <TrustFactorsManager {...props} />;
      case 'AnalyticsManager':
        return <AnalyticsManager {...props} />;
      case 'NewsBrandsManager':
        return <NewsBrandsManager {...props} />;
      case 'PaymentOptionsManager':
        return <PaymentOptionsManager {...props} />;
      case 'TestimonialsManager':
        return <TestimonialsManager {...props} />;
      case 'ExpertsManager':
        return <ExpertsManager {...props} />;
      case 'FAQManager':
        return <FAQManager {...props} />;
      case 'SingleSectionEditor':
        return <SingleSectionEditor {...props} />;
      case 'NestedSectionEditor':
        return <NestedSectionEditor {...props} />;
      case 'ListManager':
        return <ListManager {...props} config={config} />;
      default:
        return null;
    }
  };

  if (loading && viewMode === 'list') {
    return <Loader loading={loading} />;
  }

  if (viewMode === 'edit') {
    return renderSectionManager();
  }

  // Get section icon based on type
  const getSectionIcon = (sectionKey, sectionType) => {
    const icons = {
      hero_images: 'fa-images',
      hero_right_section: 'fa-align-right',
      trust_factors: 'fa-shield-alt',
      how_it_works: 'fa-list-ol',
      testimonials: 'fa-comments',
      why_tryageless: 'fa-star',
      analytics: 'fa-chart-line',
      news_brands: 'fa-newspaper-o',
      experts: 'fa-user-md',
      proud_section: 'fa-trophy',
      comparison: 'fa-balance-scale',
      faq: 'fa-question-circle',
      caring_section: 'fa-heart',
      payment_options: 'fa-credit-card',
    };
    return icons[sectionKey] || 'fa-file';
  };

  // Get badge color based on section type
  const getBadgeColor = (sectionType) => {
    const colors = {
      list: 'primary',
      single: 'success',
      nested: 'warning',
    };
    return colors[sectionType] || 'secondary';
  };

  return (
    <div className="content_wrapper all_products_main">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              <div className="header_top border-bottom pb-3">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h3 className="mb-1">
                      <i className="fa fa-home me-2 text-primary"></i>
                      Landing Page CMS
                    </h3>
                    <p className="text-muted mb-0">
                      Centralized management for all homepage sections - control content, order, and visibility
                    </p>
                    {landingPageEnabled && (
                      <div className="mt-2">
                        <span className="badge bg-success">
                          <i className="fa fa-check-circle me-1"></i>
                          New Landing Page Active
                        </span>
                      </div>
                    )}
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="text-end">
                      <small className="text-muted d-block">Total Sections</small>
                      <h4 className="mb-0 text-primary">{sections.length}</h4>
                    </div>
                    <button
                      className="btn btn-outline-primary"
                      onClick={() => setShowSettings(true)}
                      style={{ minWidth: '120px' }}
                    >
                      <i className="fa fa-cog me-2"></i>
                      Settings
                    </button>
                  </div>
                </div>
              </div>

              <div className="middle_main pt-4">
                {sections.length === 0 ? (
                  <div className="text-center py-5">
                    <i className="fa fa-folder-open fa-4x text-muted mb-3 d-block"></i>
                    <h5 className="text-muted">No sections found</h5>
                    <p className="text-muted">Please check your backend configuration</p>
                  </div>
                ) : (
                  <div className="row g-3">
                    {sections.map((section) => {
                      const config = SECTION_CONFIG[section.sectionKey];
                      const itemCount =
                        section.sectionType === 'list' ? section.items?.length || 0 : '-';

                      return (
                        <div key={section._id} className="col-12 mb-3">
                          <div
                            className="card border-0 shadow-sm"
                            style={{
                              transition: 'all 0.3s ease',
                              cursor: 'pointer',
                            }}
                            onMouseEnter={(e) => {
                              e.currentTarget.style.transform = 'translateY(-2px)';
                              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.transform = 'translateY(0)';
                              e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                            }}
                          >
                            <div className="card-body">
                              <div className="row align-items-center">
                                {/* Icon Column */}
                                <div className="col-auto">
                                  <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{
                                      width: '60px',
                                      height: '60px',
                                      backgroundColor: '#f8f9fa',
                                      borderRadius: '12px',
                                    }}
                                  >
                                    <i
                                      className={`fa ${getSectionIcon(section.sectionKey, section.sectionType)} fa-2x text-primary`}
                                    ></i>
                                  </div>
                                </div>

                                {/* Section Info Column */}
                                <div className="col">
                                  <div className="d-flex align-items-center mb-1">
                                    <h5 className="mb-0 me-2 fw-bold">
                                      {config?.name || section.sectionName}
                                    </h5>
                                    <span
                                      className={`badge bg-${getBadgeColor(section.sectionType)} text-uppercase me-2`}
                                      style={{ fontSize: '0.65rem', fontWeight: '600' }}
                                    >
                                      {section.sectionType}
                                    </span>
                                    <span
                                      className={`badge ${section.status ? 'bg-success' : 'bg-secondary'}`}
                                    >
                                      <i className={`fa fa-${section.status ? 'check-circle' : 'eye-slash'} me-1`}></i>
                                      {section.status ? 'Active' : 'Hidden'}
                                    </span>
                                  </div>
                                  <div className="d-flex align-items-center gap-3">
                                    <small className="text-muted">
                                      <i className="fa fa-key me-1"></i>
                                      <code className="text-muted bg-light px-2 py-1 rounded" style={{ fontSize: '0.75rem' }}>
                                        {section.sectionKey}
                                      </code>
                                    </small>
                                    {section.sectionType === 'list' && (
                                      <small className="text-muted">
                                        <i className="fa fa-list me-1"></i>
                                        <strong>{itemCount}</strong> items
                                      </small>
                                    )}
                                  </div>
                                </div>

                                {/* Toggle Column */}
                                <div className="col-auto">
                                  <ReactSwitch
                                    uncheckedIcon={false}
                                    checkedIcon={false}
                                    onChange={() =>
                                      handleToggleStatus(section.sectionKey, section.status)
                                    }
                                    checked={section.status}
                                    height={24}
                                    width={48}
                                    onColor="#28a745"
                                    offColor="#6c757d"
                                  />
                                </div>

                                {/* Manage Button Column */}
                                <div className="col-auto">
                                  <button
                                    className="btn btn-primary"
                                    onClick={() => handleSectionClick(section)}
                                  >
                                    <i className="fa fa-cog me-2"></i>
                                    Manage Section
                                  </button>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Landing Page Settings Modal */}
      <LandingPageSettings
        show={showSettings}
        onHide={() => setShowSettings(false)}
        onSettingsUpdate={() => {
          fetchSections();
          fetchLandingPageStatus();
        }}
      />
    </div>
  );
};

// Generic List Manager Component (Placeholder for trust_factors, analytics, etc.)
const ListManager = ({ section, backToList, config }) => {
  return (
    <div className="content_wrapper">
      <div className="container-fluid">
        <div className="row">
          <div className="col-md-12">
            <ol className="breadcrumb">
              <li onClick={backToList} style={{ cursor: 'pointer' }}>
                Landing Page CMS
              </li>
              <li>{config.name}</li>
            </ol>
          </div>
        </div>
        <div className="row">
          <div className="col-md-12">
            <div className="box_main">
              <div className="header_top">
                <h3>{config.name} Management</h3>
              </div>
              <div className="middle_main">
                <p>Generic list manager for {config.name}</p>
                <p>Items: {section.items?.length || 0}</p>
                <button className="btn btn-secondary" onClick={backToList}>
                  Back to Sections
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LandingPageCMS;
